import { sleep } from './format';
import type { Order, Periodo, RouteGeometry } from '../types';

/* ─── constantes ─────────────────────────────────────────── */
export const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
export const OSRM_TRIP_URL = 'https://router.project-osrm.org/trip/v1/driving';
export const OSRM_ROUTE_URL = 'https://router.project-osrm.org/route/v1/driving';
export const GEOCODE_DELAY = 1200;

export const PERIODOS: Periodo[] = ['manha', 'comercial', 'tarde'];
export const PER_LABEL: Record<Periodo, string> = { manha: '🌅 Manhã', comercial: '🏢 Comercial', tarde: '🌇 Tarde' };
export const PER_COLOR: Record<Periodo, string> = { manha: '#0284c7', comercial: '#475569', tarde: '#ea580c' };

export const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  ok: 'Aguardando',
  concluida: '✓ Concluída',
  reagendada: '↺ Reagendada'
};
export const STATUS_COLOR: Record<string, string> = {
  pending: '#94a3b8',
  ok: '#f59e0b',
  concluida: '#16a34a',
  reagendada: '#d97706'
};
export const DEFAULT_CENTER: [number, number] = [-23.5015, -47.4526];

interface Coord {
  lat: number;
  lng: number;
}

export async function geocodeNominatim(endereco: string, cidade?: string): Promise<Coord | null> {
  const params = new URLSearchParams({ format: 'json', limit: '1' });
  const q = cidade ? `${endereco}, ${cidade}, Brasil` : `${endereco}, Brasil`;
  params.set('q', q);
  const res = await fetch(`${NOMINATIM_URL}?${params}`, { headers: { 'Accept-Language': 'pt-BR' } });
  if (!res.ok) throw new Error(`Nominatim HTTP ${res.status}`);
  const data = await res.json();
  if (!Array.isArray(data) || data.length === 0) return null;
  return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
}

async function fetchJson(url: string): Promise<any> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

interface OptimizeResult {
  order: number[];
  distance: number;
  duration: number;
  geometry: RouteGeometry | null;
}

async function optimizeOSRM(pts: Coord[]): Promise<OptimizeResult> {
  if (pts.length === 1) return { order: [0], distance: 0, duration: 0, geometry: null };
  if (pts.length === 2) {
    const coordsStr = pts.map((p) => `${p.lng},${p.lat}`).join(';');
    const data = await fetchJson(`${OSRM_ROUTE_URL}/${coordsStr}?overview=full&geometries=geojson`);
    if (data.code !== 'Ok') throw new Error(`OSRM: ${data.code}`);
    return {
      order: [0, 1],
      distance: data.routes[0].distance,
      duration: data.routes[0].duration,
      geometry: data.routes[0].geometry
    };
  }
  const coordsStr = pts.map((p) => `${p.lng},${p.lat}`).join(';');
  const trip = await fetchJson(`${OSRM_TRIP_URL}/${coordsStr}?source=first&roundtrip=false&overview=false`);
  if (trip.code !== 'Ok') throw new Error(`OSRM Trip: ${trip.code}`);
  const open = new Array(pts.length);
  trip.waypoints.forEach((w: any, i: number) => {
    open[w.waypoint_index] = i;
  });
  const ordered = open.map((i) => `${pts[i].lng},${pts[i].lat}`).join(';');
  const route = await fetchJson(`${OSRM_ROUTE_URL}/${ordered}?overview=full&geometries=geojson`);
  if (route.code !== 'Ok') throw new Error(`OSRM Route: ${route.code}`);
  return {
    order: open,
    distance: route.routes[0].distance,
    duration: route.routes[0].duration,
    geometry: route.routes[0].geometry
  };
}

export async function optimizeWithPeriods(
  ordersWithCoords: Order[],
  startCoords: Coord | null
): Promise<OptimizeResult> {
  const groups: Record<Periodo, (Order & { _i: number })[]> = { manha: [], comercial: [], tarde: [] };
  ordersWithCoords.forEach((o, i) =>
    groups[PERIODOS.includes(o.periodo) ? o.periodo : 'comercial'].push({ ...o, _i: i })
  );

  const sequence = [...groups.manha, ...groups.comercial, ...groups.tarde];
  if (sequence.length === 0) return { order: [], distance: 0, duration: 0, geometry: null };

  const pts: Coord[] = startCoords
    ? [startCoords, ...sequence.map((o) => ({ lat: o.lat as number, lng: o.lng as number }))]
    : sequence.map((o) => ({ lat: o.lat as number, lng: o.lng as number }));

  const result = await optimizeOSRM(pts);

  let order: number[];
  if (startCoords) {
    order = result.order.filter((i) => i !== 0).map((i) => sequence[i - 1]._i);
  } else {
    order = result.order.map((i) => sequence[i]._i);
  }
  return { order, distance: result.distance, duration: result.duration, geometry: result.geometry };
}

export { sleep };
